import { ApexOptions } from "apexcharts"
import { useMemo, useState } from "react"
import ReactApexChart from "react-apexcharts"
import { type MarginGrain, formatMargin, marginTrend } from "../../../marginTrend"

/*
    Margin Trend (MPN-1530 §4.4).

    Weekly / Monthly / Overall switches the aggregation grain of the SAME calculation the
    Full Campaign panel shows, so "Overall" reproduces that panel's number by construction
    rather than by coincidence.

    Bars rather than a line: margin is a per-bucket verdict, not a continuous quantity
    moving through time, and a line between two calendar months implies values in between
    that do not exist. Coloured by sign, because over and under pace are the two things
    anyone reads this chart for.
*/

const GRAINS: { key: MarginGrain; label: string }[] = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "overall", label: "Overall" },
]

const UNDER = "#22c55e" // under pace, budget still in hand
const OVER = "#ef4444"  // over pace

export default function MarginTrend({ data }: { data: any }) {
  const [grain, setGrain] = useState<MarginGrain>("weekly")

  const { series, options, points } = useMemo(() => {
    const points = marginTrend(data, grain)

    const options: ApexOptions = {
      chart: {
        type: "bar",
        height: 300,
        toolbar: { show: false },
        animations: { enabled: false },
        zoom: { enabled: false },
      },
      plotOptions: {
        bar: {
          columnWidth: grain === "overall" ? "22%" : "62%",
          // One colour per bar by sign, rather than one colour for the series.
          distributed: true,
        },
      },
      colors: points.map((p) => ((p.margin ?? 0) < 0 ? OVER : UNDER)),
      /*
          The value above each bar, as the supplied mockup has it. Margin is the number
          being read here, and making someone hover every bar to learn it defeats the
          chart. Signed, so over and under pace are distinguishable at a glance.
      */
      dataLabels: {
        enabled: true,
        offsetY: -20,
        formatter: (v: number) => (v == null ? "" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`),
        style: { fontSize: "11px", fontWeight: 600, colors: ["#475569"] },
      },
      legend: { show: false }, // `distributed` would otherwise legend every single bar
      xaxis: {
        categories: points.map((p) => p.label),
        labels: { rotate: 0, hideOverlappingLabels: true },
      },
      yaxis: {
        labels: { formatter: (v: number) => `${Math.round(v)}%` },
        title: { text: "Margin" },
        // Headroom so a label above the tallest bar is not clipped by the plot edge.
        forceNiceScale: true,
      },
      grid: { borderColor: "#f1f1f1", padding: { top: 16 } },
      tooltip: {
        // Spend against pace, because the percentage alone never says which way it got
        // there: a small bucket overspending slightly and a big one overspending a lot
        // can print the same number.
        custom: ({ dataPointIndex }: any) => {
          const p = points[dataPointIndex]
          if (!p) return ""
          return `
            <div class="mon-tip">
              <div class="mon-tip__head">${p.label}</div>
              <div class="mon-tip__row">
                <span class="mon-tip__name">Spent</span>
                <span class="mon-tip__val">$${p.spend.toFixed(2)}</span>
              </div>
              <div class="mon-tip__row">
                <span class="mon-tip__name">Pace (${p.days} ${p.days === 1 ? "day" : "days"})</span>
                <span class="mon-tip__val">$${p.prorated.toFixed(2)}</span>
              </div>
              <div class="mon-tip__total">
                <span class="mon-tip__name">Margin</span>
                <span class="mon-tip__val">${formatMargin(p.margin)}</span>
              </div>
            </div>`
        },
      },
    }

    return {
      points,
      options,
      series: [{
        name: "Margin",
        data: points.map((p) => (p.margin === null ? null : +(p.margin * 100).toFixed(1))),
      }],
    }
  }, [data, grain])

  return (
    <div>
      <div className="mon-grain">
        {GRAINS.map((g) => (
          <button
            key={g.key}
            type="button"
            className={`mon-grain__btn${grain === g.key ? " mon-grain__btn--on" : ""}`}
            onClick={() => setGrain(g.key)}
          >
            {g.label}
          </button>
        ))}
      </div>

      {points.length === 0 ? (
        <p className="text-muted fs-11 mb-0">
          Margin needs a campaign budget and a schedule to prorate against. Set both on the
          Budget and Schedule pages.
        </p>
      ) : (
        <ReactApexChart options={options} series={series as any} type="bar" height={300} />
      )}
    </div>
  )
}
